import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Plus, Phone, PoundSterling, LayoutGrid, List, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import LeadModal from '@/components/LeadModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PIPELINE_STAGES = [
  { id: 'new', label: 'New', color: 'bg-sky-500', bgClass: 'bg-sky-50', textColor: 'text-sky-700' },
  { id: 'contacted', label: 'Contacted', color: 'bg-blue-500', bgClass: 'bg-blue-50', textColor: 'text-blue-700' },
  { id: 'qualified', label: 'Qualified', color: 'bg-indigo-500', bgClass: 'bg-indigo-50', textColor: 'text-indigo-700' },
  { id: 'won', label: 'Won', color: 'bg-emerald-500', bgClass: 'bg-emerald-50', textColor: 'text-emerald-700' },
  { id: 'lost', label: 'Lost', color: 'bg-red-500', bgClass: 'bg-red-50', textColor: 'text-red-700' },
];

const PipelinePage = () => {
  const { selectedCampaign, user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [draggedLead, setDraggedLead] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'kanban' or 'list'
  const [selectedStageFilter, setSelectedStageFilter] = useState('all');

  const fetchLeads = useCallback(async () => {
    if (!selectedCampaign) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(
        `${API_URL}/api/leads?campaign_id=${selectedCampaign.campaign_id}&limit=500`,
        { credentials: 'include', headers }
      );

      if (response.ok) {
        setLeads(await response.json());
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load pipeline');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCampaign]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    if (!draggedLead || draggedLead.pipeline_stage === newStage) {
      setDraggedLead(null);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const response = await fetch(`${API_URL}/api/leads/${draggedLead.lead_id}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ pipeline_stage: newStage })
      });

      if (response.ok) {
        toast.success(`Moved to ${newStage}`);
        fetchLeads();
      } else {
        toast.error('Failed to update lead');
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update lead');
    }
    
    setDraggedLead(null);
  };

  const handleStageChange = async (lead, newStage) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const response = await fetch(`${API_URL}/api/leads/${lead.lead_id}`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({ pipeline_stage: newStage })
      });

      if (response.ok) {
        toast.success(`Moved to ${newStage}`);
        fetchLeads();
      } else {
        toast.error('Failed to update lead');
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update lead');
    }
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  const handleLeadUpdate = () => {
    fetchLeads();
    setShowLeadModal(false);
    setSelectedLead(null);
  };

  const getLeadsByStage = (stageId) => {
    return leads.filter(l => l.pipeline_stage === stageId);
  };

  const getStageValue = (stageId) => {
    return leads
      .filter(l => l.pipeline_stage === stageId)
      .reduce((sum, l) => sum + (l.revenue || 0), 0);
  };

  const getFilteredLeads = () => {
    if (selectedStageFilter === 'all') return leads;
    return leads.filter(l => l.pipeline_stage === selectedStageFilter);
  };

  if (!selectedCampaign) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center px-4">
          <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">No Campaign Selected</h2>
          <p className="text-slate-500 mt-2">Please select a campaign from the sidebar</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-[#00B4D8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col animate-fade-in" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-4 sm:mb-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Pipeline
            </h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1 truncate">{selectedCampaign.name}</p>
          </div>
          <Button 
            onClick={() => { setSelectedLead(null); setShowLeadModal(true); }}
            className="bg-[#00B4D8] hover:bg-[#009AC0] text-white min-h-[44px] px-3 sm:px-4 flex-shrink-0"
            data-testid="pipeline-add-lead-btn"
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Lead</span>
          </Button>
        </div>

        {/* View Toggle & Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Stage Filter (Mobile) */}
          <div className="flex-1 sm:max-w-[200px]">
            <Select value={selectedStageFilter} onValueChange={setSelectedStageFilter}>
              <SelectTrigger className="w-full min-h-[44px]">
                <SelectValue placeholder="Filter by stage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {PIPELINE_STAGES.map(stage => (
                  <SelectItem key={stage.id} value={stage.id}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${stage.color}`}></div>
                      {stage.label} ({getLeadsByStage(stage.id).length})
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* View Mode Toggle */}
          <div className="flex bg-slate-100 rounded-lg p-1 self-start">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors min-h-[40px] ${
                viewMode === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
              }`}
            >
              <List className="w-4 h-4" />
              <span className="hidden xs:inline">List</span>
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors min-h-[40px] ${
                viewMode === 'kanban' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="hidden xs:inline">Board</span>
            </button>
          </div>
        </div>

        {/* Stage Summary Cards (Mobile) */}
        <div className="grid grid-cols-5 gap-2 sm:hidden">
          {PIPELINE_STAGES.map(stage => (
            <button
              key={stage.id}
              onClick={() => setSelectedStageFilter(stage.id === selectedStageFilter ? 'all' : stage.id)}
              className={`p-2 rounded-lg text-center transition-all ${
                selectedStageFilter === stage.id ? stage.bgClass + ' ring-2 ring-offset-1 ring-slate-400' : 'bg-slate-100'
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${stage.color} mx-auto mb-1`}></div>
              <div className="text-xs font-medium text-slate-700">{getLeadsByStage(stage.id).length}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {viewMode === 'kanban' ? (
        /* Kanban Board View - Horizontal Scroll on Mobile */
        <div className="flex-1 overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex gap-3 sm:gap-4 min-w-max">
            {PIPELINE_STAGES.map((stage) => {
              const stageLeads = getLeadsByStage(stage.id);
              const stageValue = getStageValue(stage.id);
              
              return (
                <div 
                  key={stage.id}
                  className="w-[260px] sm:w-[300px] flex flex-col max-h-[calc(100vh-280px)] sm:max-h-[calc(100vh-220px)]"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage.id)}
                  data-testid={`pipeline-column-${stage.id}`}
                >
                  {/* Column Header */}
                  <div className={`${stage.bgClass} rounded-t-xl p-3 sm:p-4 border border-b-0 border-slate-200`}>
                    <div className="flex items-center justify-between mb-1 sm:mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 sm:w-3 h-2.5 sm:h-3 rounded-full ${stage.color}`}></div>
                        <h3 className="font-semibold text-slate-900 text-sm sm:text-base">{stage.label}</h3>
                      </div>
                      <Badge variant="secondary" className="bg-white text-xs">
                        {stageLeads.length}
                      </Badge>
                    </div>
                    {isSuperAdmin && (
                      <div className="text-xs sm:text-sm text-slate-600 flex items-center gap-1">
                        <PoundSterling className="w-3 h-3" />
                        {stageValue.toLocaleString()}
                      </div>
                    )}
                  </div>

                  {/* Column Body */}
                  <div className="flex-1 bg-slate-50/50 rounded-b-xl p-2 sm:p-3 border border-t-0 border-slate-200 overflow-y-auto space-y-2 sm:space-y-3">
                    {stageLeads.length === 0 ? (
                      <div className="text-center py-6 sm:py-8 text-slate-400 text-xs sm:text-sm">
                        No leads in this stage
                      </div>
                    ) : (
                      stageLeads.map((lead) => (
                        <PipelineCard 
                          key={lead.lead_id}
                          lead={lead}
                          onDragStart={handleDragStart}
                          onClick={() => handleLeadClick(lead)}
                          isDragging={draggedLead?.lead_id === lead.lead_id}
                        />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* List View - Mobile Optimized */
        <div className="space-y-3">
          {getFilteredLeads().length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>No leads found</p>
            </div>
          ) : (
            getFilteredLeads().map((lead) => (
              <MobileLeadCard 
                key={lead.lead_id}
                lead={lead}
                onStageChange={handleStageChange}
                onClick={() => handleLeadClick(lead)}
              />
            ))
          )}
        </div>
      )}

      {/* Lead Modal */}
      <LeadModal 
        isOpen={showLeadModal}
        onClose={() => { setShowLeadModal(false); setSelectedLead(null); }}
        lead={selectedLead}
        campaignId={selectedCampaign?.campaign_id}
        onUpdate={handleLeadUpdate}
      />
    </div>
  );
};

/* Desktop Kanban Card */
const PipelineCard = ({ lead, onDragStart, onClick, isDragging }) => {
  const statusColors = {
    new: 'status-new',
    interested: 'status-interested',
    not_interested: 'status-not-interested',
    callback: 'status-callback',
    contacted: 'status-contacted',
    qualified: 'status-qualified',
    won: 'status-won',
    lost: 'status-lost'
  };

  return (
    <div 
      className={`pipeline-card cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-50' : ''}`}
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onClick={onClick}
      data-testid={`pipeline-card-${lead.lead_id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-slate-900 truncate flex-1 text-sm sm:text-base">{lead.name}</h4>
        <Badge className={`${statusColors[lead.status] || 'status-new'} text-xs ml-2 flex-shrink-0`}>
          {lead.status?.replace('_', ' ')}
        </Badge>
      </div>
      
      {lead.phone && (
        <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500 mb-2">
          <Phone className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{lead.phone}</span>
        </div>
      )}
      
      <div className="flex items-center justify-between mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-slate-100">
        {isSuperAdmin && (
          <div className="flex items-center gap-1 text-xs sm:text-sm font-medium text-slate-700">
            <PoundSterling className="w-3 h-3" />
            {(lead.revenue || 0).toLocaleString()}
          </div>
        )}
        <span className="text-xs text-slate-400 capitalize">{lead.source}</span>
      </div>
      
      {lead.callback_date && (
        <div className="mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
          Callback: {new Date(lead.callback_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
        </div>
      )}
    </div>
  );
};

/* Mobile-Optimized List Card */
const MobileLeadCard = ({ lead, onStageChange, onClick }) => {
  const currentStage = PIPELINE_STAGES.find(s => s.id === lead.pipeline_stage) || PIPELINE_STAGES[0];
  
  const statusColors = {
    new: 'status-new',
    interested: 'status-interested',
    not_interested: 'status-not-interested',
    callback: 'status-callback',
  };

  return (
    <div 
      className="bg-white rounded-xl border border-slate-200 p-4 active:bg-slate-50 transition-colors"
      data-testid={`mobile-lead-card-${lead.lead_id}`}
    >
      {/* Top Row - Name & Stage */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0" onClick={onClick}>
          <h4 className="font-semibold text-slate-900 truncate text-base">{lead.name}</h4>
          {lead.phone && (
            <p className="text-sm text-slate-500 truncate mt-0.5">{lead.phone}</p>
          )}
        </div>
        <Select 
          value={lead.pipeline_stage} 
          onValueChange={(value) => onStageChange(lead, value)}
        >
          <SelectTrigger className={`w-auto min-w-[110px] h-9 ${currentStage.bgClass} ${currentStage.textColor} border-0 font-medium text-sm`}>
            <div className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${currentStage.color}`}></div>
              <SelectValue />
            </div>
          </SelectTrigger>
          <SelectContent>
            {PIPELINE_STAGES.map(stage => (
              <SelectItem key={stage.id} value={stage.id}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${stage.color}`}></div>
                  {stage.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Middle Row - Status & Value */}
      <div className="flex items-center justify-between mb-3" onClick={onClick}>
        <Badge className={`${statusColors[lead.status] || 'status-new'} text-xs`}>
          {lead.status?.replace('_', ' ')}
        </Badge>
        {isSuperAdmin && (
          <div className="flex items-center gap-1 text-sm font-semibold text-slate-700">
            <PoundSterling className="w-3.5 h-3.5" />
            {(lead.revenue || 0).toLocaleString()}
          </div>
        )}
      </div>

      {/* Bottom Row - Callback & Source */}
      <div className="flex items-center justify-between text-xs" onClick={onClick}>
        {lead.callback_date ? (
          <span className="text-amber-600 bg-amber-50 px-2 py-1 rounded font-medium">
            Callback: {new Date(lead.callback_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
          </span>
        ) : (
          <span className="text-slate-400 capitalize">{lead.source}</span>
        )}
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </div>
    </div>
  );
};

export default PipelinePage;
